import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    TicketIcon,
    MapPinIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    XCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EllipsisVerticalIcon,
    ArrowPathIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getBookings, cancelBooking, refundBooking } from "../api/rideBookingsTripsApi";
import { extractApiError } from "@/lib/api-error";
import type { BookingsQueryParams, BookingStatus, PaymentStatus } from "../types";

// ============================================================================
// Constants
// ============================================================================

const statusOptions: { value: BookingStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "PENDING_PAYMENT", label: "Pending Payment" },
    { value: "CONFIRMED", label: "Confirmed" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "EXPIRED_NO_SHOW", label: "Expired / No Show" },
];

const paymentOptions: { value: PaymentStatus | ""; label: string }[] = [
    { value: "", label: "All Payments" },
    { value: "PENDING", label: "Pending" },
    { value: "PAID", label: "Paid" },
    { value: "REFUNDED", label: "Refunded" },
    { value: "FAILED", label: "Failed" },
];

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING_PAYMENT: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        CONFIRMED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        IN_PROGRESS: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        COMPLETED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        EXPIRED_NO_SHOW: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s.replace(/_/g, " ")}
        </span>
    );
};

const PaymentBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        PAID: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        REFUNDED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const BookingsRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const [filters, setFilters] = useState<BookingsQueryParams>({
        page: 0,
        limit: 20,
        status: "",
        paymentStatus: "",
        search: "",
        dateFrom: "",
        dateTo: "",
    });
    const [showFilters, setShowFilters] = useState(false);
    const [actionMenu, setActionMenu] = useState<string | null>(null);
    const [cancelModal, setCancelModal] = useState<{ id: string; riderName: string } | null>(null);
    const [refundModal, setRefundModal] = useState<{ id: string; riderName: string; totalAmount: number; currency: string } | null>(null);
    const [actionReason, setActionReason] = useState("");
    const [refundAmount, setRefundAmount] = useState<string>("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["bookings", filters],
        queryFn: () => getBookings(filters),
    });

    const cancelMutation = useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelBooking(id, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bookings"] });
            setCancelModal(null);
            setActionReason("");
        },
    });

    const refundMutation = useMutation({
        mutationFn: ({ id, reason, amount }: { id: string; reason: string; amount?: number }) =>
            refundBooking(id, reason, amount),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bookings"] });
            setRefundModal(null);
            setActionReason("");
            setRefundAmount("");
        },
    });

    const bookings = data?.data?.bookings ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const totalElements = data?.data?.totalElements ?? 0;
    const currentPage = filters.page ?? 0;
    const canManageBookings = hasPermission(SECTION_PERMISSIONS.RIDE_BOOKINGS_MANAGE[0]);
    const canRefundBookings = hasPermission(SECTION_PERMISSIONS.RIDE_BOOKINGS_REFUND[0]);
    const showActionsColumn = canManageBookings || canRefundBookings;

    const updateFilter = (key: keyof BookingsQueryParams, value: string) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: 0 }));
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Bookings</h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {totalElements} booking{totalElements !== 1 ? "s" : ""} total
                </p>
            </div>

            {/* Search & Filters */}
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search pickup/dropoff addresses..."
                            value={filters.search}
                            onChange={(e) => updateFilter("search", e.target.value)}
                            className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500"
                        />
                    </div>
                    <select
                        value={filters.status}
                        onChange={(e) => updateFilter("status", e.target.value)}
                        className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    >
                        {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <select
                        value={filters.paymentStatus}
                        onChange={(e) => updateFilter("paymentStatus", e.target.value)}
                        className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    >
                        {paymentOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowFilters((p) => !p)}
                        className={showFilters ? "border-primary-500 text-primary-600" : ""}
                    >
                        <FunnelIcon className="h-4 w-4" />
                    </Button>
                </div>

                {showFilters && (
                    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">From</label>
                            <input
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => updateFilter("dateFrom", e.target.value)}
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">To</label>
                            <input
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => updateFilter("dateTo", e.target.value)}
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                            />
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFilters({ page: 0, limit: 20, status: "", paymentStatus: "", search: "", dateFrom: "", dateTo: "" })}
                        >
                            Clear All
                        </Button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                    </div>
                ) : error ? (
                    <div className="px-6 py-20 text-center">
                        <XCircleIcon className="mx-auto h-10 w-10 text-rose-400" />
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{extractApiError(error)}</p>
                    </div>
                ) : bookings.length === 0 ? (
                    <div className="px-6 py-20 text-center">
                        <TicketIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                        <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">No bookings found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-800/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Rider</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Driver</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Route</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Departure</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Seats</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Amount</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Payment</th>
                                    {showActionsColumn ? (
                                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {bookings.map((booking) => (
                                    <tr
                                        key={booking.bookingId}
                                        onClick={() => navigate(`/ride-bookings-trips/bookings/${booking.bookingId}`)}
                                        className="cursor-pointer transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <UserCircleIcon className="h-4 w-4 text-slate-400" />
                                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{booking.riderName}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{booking.driverName}</td>
                                        <td className="px-4 py-3">
                                            <div className="max-w-[180px]">
                                                <div className="flex items-center gap-1.5">
                                                    <MapPinIcon className="h-3 w-3 shrink-0 text-emerald-500" />
                                                    <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-300">{booking.rideOriginAddress || booking.pickupAddress}</p>
                                                </div>
                                                <div className="mt-0.5 flex items-center gap-1.5">
                                                    <MapPinIcon className="h-3 w-3 shrink-0 text-rose-500" />
                                                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">{booking.rideDestinationAddress || booking.dropoffAddress}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                                <CalendarIcon className="h-3 w-3" />
                                                {booking.rideDepartureDate} {booking.rideDepartureTime?.slice(0, 5)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm font-medium text-slate-700 dark:text-slate-300">{booking.numberOfSeats}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                <CurrencyDollarIcon className="h-3.5 w-3.5 text-emerald-500" />
                                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{formatCurrency(booking.totalPrice, booking.currency)}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge status={booking.bookingStatus} /></td>
                                        <td className="px-4 py-3"><PaymentBadge status={booking.paymentStatus} /></td>
                                        {showActionsColumn ? (
                                            <td className="px-4 py-3 text-right">
                                                <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => setActionMenu(actionMenu === booking.bookingId ? null : booking.bookingId)}
                                                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                                                    >
                                                        <EllipsisVerticalIcon className="h-5 w-5" />
                                                    </button>
                                                    {actionMenu === booking.bookingId && (
                                                        <div className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                                                            <button
                                                                onClick={() => {
                                                                    navigate(`/ride-bookings-trips/bookings/${booking.bookingId}`);
                                                                    setActionMenu(null);
                                                                }}
                                                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                                                            >
                                                                <TicketIcon className="h-4 w-4" />
                                                                View Details
                                                            </button>
                                                            {canManageBookings &&
                                                            !["CANCELLED", "COMPLETED", "EXPIRED_NO_SHOW"].includes(booking.bookingStatus) ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setCancelModal({ id: booking.bookingId, riderName: booking.riderName });
                                                                        setActionMenu(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                                                >
                                                                    <XCircleIcon className="h-4 w-4" />
                                                                    Cancel Booking
                                                                </button>
                                                            ) : null}
                                                            {canRefundBookings &&
                                                            booking.bookingStatus === "CANCELLED" &&
                                                            booking.paymentStatus === "PAID" ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setRefundModal({ id: booking.bookingId, riderName: booking.riderName, totalAmount: booking.totalPrice, currency: booking.currency });
                                                                        setActionMenu(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                                                >
                                                                    <ArrowPathIcon className="h-4 w-4" />
                                                                    Process Refund
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Page {currentPage + 1} of {totalPages}
                        </p>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setFilters((p) => ({ ...p, page: currentPage - 1 }))}>
                                <ChevronLeftIcon className="mr-1 h-4 w-4" />Previous
                            </Button>
                            <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setFilters((p) => ({ ...p, page: currentPage + 1 }))}>
                                Next<ChevronRightIcon className="ml-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel Modal */}
            {cancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCancelModal(null); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cancel Booking</h3>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Cancel booking for <span className="font-medium text-slate-700 dark:text-slate-300">{cancelModal.riderName}</span>? Available seats will be released.
                        </p>
                        <textarea
                            rows={3}
                            placeholder="Reason for cancellation..."
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCancelModal(null); setActionReason(""); }}>Go Back</Button>
                            <Button
                                size="sm"
                                className="bg-rose-600 hover:bg-rose-700"
                                disabled={!actionReason.trim() || cancelMutation.isPending}
                                onClick={() => cancelMutation.mutate({ id: cancelModal.id, reason: actionReason })}
                            >
                                {cancelMutation.isPending ? "Cancelling..." : "Cancel Booking"}
                            </Button>
                        </div>
                        {cancelMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(cancelMutation.error)}</p>}
                    </div>
                </div>
            )}

            {/* Refund Modal */}
            {refundModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setRefundModal(null); setActionReason(""); setRefundAmount(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Process Refund</h3>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Refund <span className="font-medium text-slate-700 dark:text-slate-300">{refundModal.riderName}</span>.
                            Original amount: <span className="font-semibold text-slate-700 dark:text-slate-300">{formatCurrency(refundModal.totalAmount, refundModal.currency)}</span>
                        </p>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Refund Amount (leave empty for full refund)</label>
                                <input
                                    type="number"
                                    placeholder={refundModal.totalAmount.toString()}
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(e.target.value)}
                                    className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                />
                            </div>
                            <textarea
                                rows={3}
                                placeholder="Reason for refund..."
                                value={actionReason}
                                onChange={(e) => setActionReason(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            />
                        </div>
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setRefundModal(null); setActionReason(""); setRefundAmount(""); }}>Go Back</Button>
                            <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                disabled={refundMutation.isPending}
                                onClick={() => refundMutation.mutate({
                                    id: refundModal.id,
                                    reason: actionReason,
                                    amount: refundAmount ? parseFloat(refundAmount) : undefined,
                                })}
                            >
                                {refundMutation.isPending ? "Processing..." : "Process Refund"}
                            </Button>
                        </div>
                        {refundMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(refundMutation.error)}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};
